import styled from 'styled-components';

import {
  screenMdMin,
  screenLgMin,
  spacer4,
  spacer8,
  spacer12,
  spacer20,
  spacer24,
  spacer16,
  spacer32,
  spacer48,
  white,
  gray80,
  black,
  opacityBlack90,
  Banner,
  Type,
  screenMdMax,
} from '@spotify-internal/encore-web';
import Button from '../../atoms/Button';
import Container from '../../atoms/Container';
import Image from '../../atoms/Image/image';
import LegalTerms from '../../atoms/LegalTerms/legalTerms';

const desktop = '1024px';

const CardSection = styled.div<{
  length: number;
  isLastElement?: boolean;
}>`
  background-color: ${black};
  margin-right: auto;
  margin-left: auto;
  padding-left: ${spacer16};
  padding-right: ${spacer16};
  margin-bottom: 0px;
  border-width: 0 ${spacer4} 0 ${spacer4};
  ${({ isLastElement }) =>
    isLastElement
      ? `
    border-radius: 0 0 10px 10px;
    margin-bottom: 30px !important;
    border-width: 0 ${spacer4} ${spacer4} ${spacer4};
  `
      : ``}

  @media (min-width: ${screenLgMin}) {
    ${({ length }) =>
      length >= 2 ? 'margin: 0 0 0 0;' : 'margin: 0 auto; width: 400px;'}
    padding-left: ${spacer16};
    padding-right: ${spacer16};
  }
`;

export const Title = styled(Type.h2)`
  color: ${white};
  font-size: 32px;
  font-weight: bold;
  letter-spacing: -1.28px;
  line-height: 1;
  text-align: center;
  margin: 0;
  padding: 0;

  @media (min-width: ${screenLgMin}) {
    font-size: 40px;
    letter-spacing: -1.6px;
  }
`;

export const Description = styled(Type.h3)`
  color: #fff;
  font-size: ${spacer16};
  font-weight: normal;
  margin: ${spacer16} 0 ${spacer32};
  text-align: center;
  padding: 0;
`;

export const IconsArea = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${spacer16};
  flex-direction: column;
  align-items: center;
`;

export const PaymentIcons = styled.ul`
  display: flex;
  justify-content: center;
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const PaymentIconContainer = styled.li`
  background-color: #fff;
  padding: 5px;
  border-radius: 5px;
  /* Safari accessibility bug workaround
   * https://developer.mozilla.org/en-US/docs/Web/CSS/list-style#Accessibility_concerns
   */
  &::before {
    content: '\\200B';
  }

  margin-right: 7px;
  &:last-child {
    margin-right: 0;
  }
`;

type PaymentMethodType = {
  role?: string;
};
export const PaymentMethod = styled(Image)<PaymentMethodType>`
  width: 29px;
  height: ${spacer20};
  vertical-align: middle;
`;

export const AdditionalPaymentIconsArea = styled.div`
  position: relative;
`;
// @ts-ignore
export const AdditionalPaymentIconsContainer = styled(PaymentIcons)<{
  amount: number;
}>`
  display: none;
  position: absolute;
  top: 115%;
  left: 50%;
  padding: ${spacer8} 6px;
  background-color: ${white};
  box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  transform: translateX(-50%);
  z-index: 1;

  grid-template-columns: repeat(
    ${({ amount }) => (amount < 4 ? amount : 4)},
    1fr
  );
  column-gap: ${spacer4};
  row-gap: ${spacer4};

  & ${PaymentIconContainer} {
    margin-right: auto;
  }
`;
export const AdditionalPaymentIconsSummary = styled.div`
  color: ${white};
  font-size: 16px;
  line-height: ${spacer32};
  text-decoration: underline;
  margin-top: ${spacer4};
  margin-left: ${spacer8};
  &:hover,
  &:focus {
    & ${AdditionalPaymentIconsContainer} {
      display: grid;
    }
  }
`;

export const Header = styled(Type.h3)`
  font-size: ${spacer24};
  font-weight: bold;
  line-height: 145%;
  margin: 0;
  padding: 0;
  height: 100%;
`;

export const Subheader = styled(Type.h2)`
  font-weight: bold;
  font-size: 18px;
  line-height: ${spacer24};
  margin-top: ${spacer16};
  text-align: center;

  @media (min-width: ${desktop}) {
    margin-top: ${spacer24};
  }
`;

export const StorefrontBenefits = styled.aside<{
  gradientStart: string;
  gradientEnd: string;
}>`
  display: flex;
  align-items: center;
  padding: 0px 30px;
  box-sizing: border-box;
  color: #fff;
  flex-direction: column;
  margin: ${spacer32} auto ${spacer48};
  text-align: inherit;

  background: linear-gradient(#121212, #121212) padding-box,
    ${({ gradientStart, gradientEnd }) =>
      !!gradientStart &&
      !!gradientEnd &&
      `linear-gradient(to right, ${gradientStart}, ${gradientEnd} ), 
      border-box;
      border-width: 2px 0px;
      border-left: 2px solid ${gradientStart} !important;
      border-right: 2px solid ${gradientEnd} !important;`};
  border-radius: ${spacer12};
  border: 2px solid transparent;

  @media (min-width: ${screenMdMin}) {
    padding: 15px 30px;

    background: linear-gradient(#121212, #121212) padding-box,
      ${({ gradientStart, gradientEnd }) =>
        !!gradientStart &&
        !!gradientEnd &&
        `linear-gradient(to right, ${gradientStart}, ${gradientEnd} ), 
      border-box;
      border-width: 4px 0px;
      border-left: 4px solid ${gradientStart} !important;
      border-right: 4px solid ${gradientEnd} !important;`};
    border-radius: ${spacer12};
    border: 4px solid transparent;
  }

  @media (max-width: ${screenLgMin}) {
    max-width: 450px;
  }
`;

export const StorefrontBenefitsHeading = styled(Type.h3)`
  color: #fff;
  font-size: 16px;
  margin: 10px 0;
  text-align: inherit;
  font-weight: 700;
  padding: 0;
`;

export const StorefrontBenefitsList = styled.ul`
  padding: 0;
`;

export const StorefrontListBenefit = styled.li`
  list-style: none;
  margin-left: ${spacer20};
  position: relative;
  &:before {
    content: '✓';
    left: -${spacer20};
    position: absolute;
  }
`;
// @ts-ignore
export const Intro = styled(CardSection)<{
  column: number;
  $planColor: string;
  currentPlanVisible: boolean;
  length: number;
  headerLength: number;
  hasDropShadow: boolean;
}>`
  ${({ $planColor }) => !!$planColor && `border-color: ${$planColor};`}
  border-width: ${spacer4} ${spacer4} 0px ${spacer4};
  position: relative;
  color: ${white};

  > div {
    width: 100%;
    display: grid;
    ${({ headerLength }) =>
      headerLength > 11 ? `align-items:flex-end;` : `h3{line-height:19px;}`}
    grid-template-columns: 65% 35%;
  }

  border-radius: 10px 10px 0 0;
  padding-top: ${spacer16};
  display: grid;
  flex-flow: column nowrap;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;

  @media (min-width: ${screenLgMin}) {
    /* stylelint-disable property-no-vendor-prefix */
    -ms-grid-row: 1;
    -ms-grid-column: span 2;
    /* stylelint-enable */
    grid-row: 1;
    grid-column: span 2;
    padding-top: ${spacer16};

    &:nth-child(13),
    &:nth-child(17) {
      grid-row: 5;
      ${({ column, length }) =>
        column === 4 && column === length
          ? `grid-column-end: -3;`
          : column === 4 && `grid-column-end: -4;`}
      ${({ column, length }) =>
        column === 5 && column === length && `grid-column-end: -2;`}
    }
  }

  ${({ currentPlanVisible }) =>
    currentPlanVisible &&
    `&::before {
        box-sizing: border-box;
        background: var(--inverted-background-base,${black});
        color: var(--inverted-text-base,${white});
        content: attr(data-current-plan-text);
        padding: 5px 10px;
        position: absolute;
        left: 4%;
        font-size: 14px;
        font-weight: 700;
        line-height: ${spacer20};
        bottom: 103%;
        border-radius: ${spacer4} ${spacer4} 0 0;
        width: auto;
        text-align: inherit;
        border:none;
    }`}
`;

export const SmallText = styled.p`
  color: ${white};
  display: inline-block;
  text-align: right;
  text-transform: uppercase;
  // display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 10px;
  margin: 0 0 0;
  word-spacing: unset;
  letter-spacing: 0.1em;
  font-weight: 400;
  line-height: ${spacer24};

  &::first-line {
    word-spacing: 9999rem;
    margin-top: 5px;

    display: block;

    font-size: ${spacer24};
    font-weight: 700;
    // line-height: 23px;

    // text-transform: uppercase;
  }
`;

export const Benefit = styled.li`
  display: flex;
  margin-bottom: 7px;

  > div {
    &:before {
      content: '•';
      display: block;
      color: #fff;
    }
    > svg {
      display: none;
    }
  }
`;

export const BenefitText = styled.p<{
  isTimedOffer?: boolean;
  offerBackground?: string;
  textColor?: string;
}>`
  ${({ isTimedOffer, textColor, offerBackground }) =>
    !!isTimedOffer
      ? `color: ${textColor ? textColor : black}; 
         background:${offerBackground ? offerBackground : white};
         padding: 0 5px;`
      : `color: ${white}; padding: 0;`}
  line-height: 22px;
  list-style: none;
  margin: 0;
`;

export const List = styled(CardSection)<{
  column: number;
  $planColor: string;
  length: number;
  hasDropShadow: boolean;
}>`
  ${({ $planColor }) => $planColor && `border-color: ${$planColor};`}

  padding-top: ${spacer20};
  padding-bottom: ${spacer20};
  list-style: none;

  @media (min-width: ${screenLgMin}) {
    /* stylelint-disable property-no-vendor-prefix */
    -ms-grid-row: 2;
    -ms-grid-column: span 2;
    /* stylelint-enable */
    grid-row: 2;
    grid-column: span 2;
    padding-top: ${spacer20};
    &:nth-child(14),
    &:nth-child(18) {
      grid-row: 6;
      ${({ column, length }) =>
        column === 4 && column === length
          ? `grid-column-end: -3;`
          : column === 4 && `grid-column-end: -4;`}
      ${({ column, length }) =>
        column === 5 && column === length && `grid-column-end: -2;`}
    }
  }
`;

export const StyledDivider = styled.hr`
  align-self: stretch;
  border-color: ${gray80};
  margin: auto 0 0;
`;

export const LegalDisclaimerWrapper = styled(CardSection)<{
  column: number;
  $planColor: string;
  length: number;
  hasDropShadow: boolean;
}>`
  ${({ $planColor }) => $planColor && `border-color: ${$planColor};`}
  border-width: 0 ${spacer4} ${spacer4} ${spacer4};
  border-radius: 0 0 10px 10px;

  padding-top: ${spacer4};
  padding-bottom: ${spacer16};
  margin-bottom: 30px;

  @media (min-width: ${screenLgMin}) {
    /* stylelint-disable property-no-vendor-prefix */
    -ms-grid-row: 4;
    -ms-grid-column: span 2;
    /* stylelint-enable */
    grid-row: 4;
    grid-column: span 2;
    padding-top: ${spacer4};
    padding-bottom: ${spacer16};
    &:nth-child(16),
    &:nth-child(20) {
      grid-row: 8;
      ${({ column, length }) =>
        column === 4 && column === length
          ? `grid-column-end: -3;`
          : column === 4 && `grid-column-end: -4;`}
      ${({ column, length }) =>
        column === 5 && column === length && `grid-column-end: -2;`}
    }
  }
`;

export const LegalDisclaimer = styled(LegalTerms)<{
  defaultDisclaimer?: boolean;
  additionalPlans?: boolean;
  isGroupLegalDisclaimer?: boolean;
}>`
  text-align: center;
  padding-top: ${spacer12};
  color: ${({ isGroupLegalDisclaimer }) =>
    isGroupLegalDisclaimer ? opacityBlack90 : white};
  font-size: 12px;
  line-height: ${spacer16};
  font-weight: 400;
  ${({ defaultDisclaimer }) => (defaultDisclaimer ? 'padding-top: 14px;' : '')}
  ${({ defaultDisclaimer, additionalPlans }) =>
    defaultDisclaimer && additionalPlans ? 'padding-bottom: 28px;' : ''}

  > a {
    &:hover {
      font-weight: bold;
    }
    color: ${({ isGroupLegalDisclaimer }) =>
      isGroupLegalDisclaimer ? opacityBlack90 : white};
    text-decoration: underline;
  }
`;

// fullWidth prop blocks the hover behavior of buttons
export const PrimaryButton = styled(Button)<{ $planColor: string }>`
  &:hover {
    transform: scale(1.04);
  }
  color: ${black};
  ${({ $planColor }) => $planColor && `background: ${$planColor}!important;`}
`;

export const SecondaryButton = styled(Button)<{ $planColor: string }>`
  margin: 0;
  padding: 0px;
  &:hover {
    transform: scale(1.04);
  }
  background: ${black} !important;
  ${({ $planColor }) => $planColor && `color: ${$planColor}!important;`}
  span {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;
// @ts-ignore
export const ButtonContainer = styled(CardSection)<{
  column: number;
  length: number;
  $planColor: string;
  hasDropShadow: boolean;
}>`
  flex: 1;
  ${({ $planColor }) => $planColor && `border-color: ${$planColor};`}

  padding-bottom: ${({ isLastElement }) => (isLastElement ? '36px' : 0)};
  text-align: center;

  > div:nth-child(2) > a {
    margin: 19px 0 14px;
    width: auto;
    border-radius: ${spacer20};
  }

  > div > a:focus-visible {
    &:focus-visible {
      outline: solid white;
      outline-width: 3px;
      outline-offset: 5px;
    }
  }

  @media (min-width: ${screenLgMin}) {
    /* stylelint-disable property-no-vendor-prefix */
    -ms-grid-row: 3;
    -ms-grid-column: span 2;
    /* stylelint-enable */
    grid-row: 3;
    grid-column: span 2;
    padding-bottom: ${({ isLastElement }) => (isLastElement ? '36px' : 0)};
    &:nth-child(15),
    &:nth-child(19) {
      grid-row: 7;
      ${({ column, length }) =>
        column === 4 && column === length
          ? `grid-column-end: -3;`
          : column === 4 && `grid-column-end: -4;`}
      ${({ column, length }) =>
        column === 5 && column === length && `grid-column-end: -2;`}
    }
  }
`;

export const Plans = styled.div<{ length: number }>`
  margin: 30px 0 26px;
  gap: 0px ${spacer32};
  display: grid;

  @media (max-width: ${screenMdMax}) {
    max-width: 450px;
    width: 100%;
    margin: auto;
  }
  > * {
    flex-direction: column;
    border-style: solid;
    box-sizing: border-box;
    width: 100%;
    max-width: 450px;
    box-shadow: none;
    background-color: transparent;
    @media (max-width: ${screenMdMax}) {
      width: 100%;
      margin-top: 0;
    }
  }

  @media (min-width: ${screenLgMin}) {
    /* stylelint-disable value-no-vendor-prefix */
    /* stylelint-disable property-no-vendor-prefix */
    /* stylelint-disable declaration-block-no-duplicate-properties */

    justify-content: center;

    ${({ length }) =>
      length >= 3
        ? `grid-template-columns: repeat(6, 1fr);`
        : `grid-template-columns: repeat(2, 1fr);`}
    justify-content: center;
  }
`;

export const StyledContainer = styled(Container)`
  word-wrap: break-word;
  > article {
    padding: 35px 25px;
  }
`;

export const Lozenges = styled.ul<{ $planColor: string }>`
  margin: 0 0 10px 0;
  padding: 0;
  flex: 100%;
  li {
    padding: 9px 7px;
    font-weight: 700;
    text-transform: capitalize;
    font-size: var(--text-base, 12px);
    line-height: 13.75px;
    border-radius: 2px;
    color: ${black};
    ${({ $planColor }) => $planColor && `background: ${$planColor};`}
  }
`;

export const ErrorBanner = styled(Banner)`
  background: #d9dadc;
  color: var(--text-inverted-base, #000);
  margin-top: ${spacer32};
  border-radius: ${spacer4};
  padding: 14px 18px;

  span[class*='Message-'] {
    display: flex;
    > div:not([role='button']) {
      padding: 0 14px 0 2px;
    }
  }

  @media (min-width: ${screenMdMin}) {
    padding: ${spacer8} ${spacer12} ${spacer4};
  }
  @media (max-width: ${screenMdMax}) {
    margin-bottom: ${spacer24};
    max-width: 450px;
    margin-left: auto;
    margin-right: auto;
  }
`;

export const ErrorMessage = styled.div`
  flex: 1;
`;

export const ExitError = styled.div`
  cursor: pointer;
`;

export const HiddenContainer = styled.div`
  display: none;
`;
